// FORME theme script. Small on purpose: every page works from Liquid, and this adds the bag, search,
// filters, gallery and calculator. 3D code only loads when a shopper reaches it.

import { calculate, round2 } from './forme-calc.js';
import { baseName, sizeFromText, sizeLabel, sameSize } from './forme-sizes.js';

export const $ = (s, r = document) => r.querySelector(s);
export const $$ = (s, r = document) => [...r.querySelectorAll(s)];
export const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const CFG = window.FORME;
const canRememberPreferences = () => window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() === true;
document.addEventListener('visitorConsentCollected', () => {
  if (canRememberPreferences()) return;
  try { ['forme.postcode', 'forme.recentSearches', 'forme.density'].forEach(key => localStorage.removeItem(key)); } catch { /* Storage may be blocked. */ }
});
export const money = (minor) => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: CFG.money }).format(minor / 100);
const ICONS = {
  close: '<path d="m4.5 4.5 11 11M15.5 4.5l-11 11"/>',
  plus: '<path d="M10 3.5v13M3.5 10h13"/>',
  minus: '<path d="M3.5 10h13"/>',
  search: '<circle cx="9" cy="9" r="5.5"/><path d="m13.2 13.2 4.3 4.3"/>',
  user: '<circle cx="10" cy="6.5" r="3.5"/><path d="M3 17.5c.6-3.6 3.5-5.5 7-5.5s6.4 1.9 7 5.5"/>',
  truck: '<path d="M1.5 5h10.5v9.5H1.5zM12 8h3.5l3 3v3.5H12"/><circle cx="5.5" cy="15" r="1.75"/><circle cx="14.5" cy="15" r="1.75"/>',
  chevron: '<path d="m7.5 4 6 6-6 6"/>',
  bag: '<path d="M4 7h12l.8 10.5H3.2L4 7Z"/><path d="M7 9V5.5a3 3 0 0 1 6 0V9"/>',
  back: '<path d="M12 4 6 10l6 6"/>',
  share: '<path d="M10 12.5v-9M6.5 6.5 10 3l3.5 3.5"/><path d="M4 11v5.5h12V11"/>',
  image: '<rect x="2.5" y="4" width="15" height="12"/><path d="m2.5 13 4-3.5 3.5 3 2.5-2 5 4"/>',
  compare: '<path d="M10 2.5v15"/><rect x="2.5" y="5" width="15" height="10"/>',
};
export const icon = (n) => `<svg class="icon" viewBox="0 0 20 20" aria-hidden="true">${ICONS[n]}</svg>`;

export function toast(msg) {
  const t = $('#toast');
  t.textContent = msg;
  t.classList.add('on');
  clearTimeout(toast.h);
  toast.h = setTimeout(() => t.classList.remove('on'), 2600);
}

// ---- Sheets -------------------------------------------------------------------------------------------
const root = $('#sheet-root');
let lastFocus = null;

// Full-screen sheets. iOS scrolls the page under a fixed layer when the keyboard opens, so the page is pinned
// and the sheet follows the visible area, which keeps the search bar at the top, just above the results.
let lockedY = null;
function lockPage() {
  lockedY = window.scrollY;
  Object.assign(document.body.style, { position: 'fixed', top: `-${lockedY}px`, left: '0', right: '0' });
}
function unlockPage() {
  if (lockedY === null) return;
  Object.assign(document.body.style, { position: '', top: '', left: '', right: '' });
  window.scrollTo({ top: lockedY, behavior: 'instant' });
  lockedY = null;
}
const vv = window.visualViewport;
function fitSheet() {
  const s = $('.sheet', root);
  if (!s || !vv) return;
  if (s.classList.contains('full')) {
    s.style.top = `${vv.offsetTop}px`;
    s.style.bottom = 'auto';
    s.style.height = `${vv.height}px`;
  } else {
    // Bottom sheets, such as the bag's postcode field, sit just above the keyboard.
    s.style.bottom = `${Math.max(0, window.innerHeight - vv.height - vv.offsetTop)}px`;
    s.style.maxHeight = `${vv.height}px`;
  }
}
vv?.addEventListener('resize', fitSheet);
vv?.addEventListener('scroll', fitSheet);

export function openSheet({ title, body, foot = '', cls = '', onClose }) {
  closeSheet(true);
  lastFocus = document.activeElement;
  root.innerHTML = `<div class="scrim" data-act="close-sheet"></div>
    <div class="sheet ${cls}" role="dialog" aria-modal="true" aria-label="${esc(title)}">
      <span class="grab"></span>
      <div class="sheet-head wrap"><h2 style="font-size:var(--t-body);font-weight:500;letter-spacing:0">${esc(title)}</h2><button class="icon-btn" style="margin-right:-9px" data-act="close-sheet" aria-label="Close">${icon('close')}</button></div>
      <div class="sheet-body wrap">${body}</div>
      ${foot ? `<div class="sheet-foot wrap">${foot}</div>` : ''}
    </div>`;
  root._onClose = onClose;
  document.body.style.overflow = 'hidden';
  if (!document.body.classList.contains('in-studio')) lockPage();
  fitSheet();
  $('.sheet [data-act=close-sheet]', root)?.focus({ preventScroll: true });
  return $('.sheet', root);
}
export function closeSheet(silent) {
  if (!root.innerHTML) return;
  root.innerHTML = '';
  if (!document.body.classList.contains('in-studio')) unlockPage();
  if (!document.body.classList.contains('in-studio')) document.body.style.overflow = '';
  const cb = root._onClose; root._onClose = null;
  if (!silent) { cb?.(); lastFocus?.focus?.({ preventScroll: true }); }
}

// ---- Cart ---------------------------------------------------------------------------------------------
const fetchCart = async () => {
  const res = await fetch(CFG.routes.cart, { cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Cart service unavailable');
  const cart = await res.json();
  if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count)) throw new Error('Invalid cart response');
  return cart;
};

function setCount(n) {
  const el = $('#bag-n');
  if (!el) return;
  el.textContent = n;
  el.hidden = !n;
}

function cartBody(cart) {
  if (!cart.items.length) return `<div class="empty"><h3>Your bag is empty</h3><p class="muted">Explore the tiles and find a look you love.</p></div>`;
  return `<div style="border-top:1px solid var(--line)">${cart.items.map((it, i) => {
    const props = Object.entries(it.properties || {}).filter(([k, v]) => v && !k.startsWith('_') && !k.startsWith('__'));
    return `<div class="line"><a class="ph" href="${esc(it.url)}">${it.image ? `<img class="in" src="${esc(it.image)}" alt="" loading="lazy">` : ''}</a><div class="line-b">
      <div class="line-t"><a href="${esc(it.url)}" style="font-weight:500">${esc(it.product_title)}</a><span class="num" data-line-price="${i}">${money(it.final_line_price)}</span></div>
      ${it.variant_title ? `<span class="small muted num">${esc(it.variant_title)}. ${money(it.final_price)} per ${['Wall Tile', 'Floor Tile'].includes(it.product_type) ? 'box' : 'item'}</span>` : ''}
      ${props.map(([k, v]) => `<span class="small">${esc(k)}: ${esc(v)}</span>`).join('')}
      <div class="line-c"><div class="stepper sm" role="group" aria-label="Quantity"><button data-act="line-step" data-line="${i + 1}" data-q="${it.quantity - 1}" aria-label="One fewer">${icon('minus')}</button><input class="num" type="number" min="0" value="${it.quantity}" data-line-input="${i + 1}" aria-label="Quantity"><button data-act="line-step" data-line="${i + 1}" data-q="${it.quantity + 1}" aria-label="One more">${icon('plus')}</button></div><button class="small link muted" style="min-height:32px" data-act="line-step" data-line="${i + 1}" data-q="0">Remove</button></div>
    </div></div>`;
  }).join('')}</div>`;
}
const cartFoot = (cart) => (cart.items.length ? `${deliveryHtml()}<div class="sum num"><div class="tot"><span>Subtotal</span><span>${money(cart.total_price)}</span></div></div><a class="btn btn-solid btn-block" href="/checkout" style="margin-top:8px;min-height:48px">Go to checkout</a><p class="small muted" style="margin-top:6px">Delivery is confirmed at checkout.</p>` : '');

// Cart data has no compare-at price, so each product is looked up once and kept for later changes to the bag.
const productLookups = new Map();
function lookupProduct(url) {
  const path = `${url.split('?')[0]}.js`;
  if (!productLookups.has(path)) productLookups.set(path, fetch(path, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  return productLookups.get(path);
}
function markOffers(cart) {
  cart.items.forEach(async (it, i) => {
    const product = await lookupProduct(it.url);
    const was = (product?.variants?.find((v) => v.id === it.variant_id)?.compare_at_price || 0) * it.quantity;
    const cell = $(`[data-line-price="${i}"]`, root);
    if (!cell || was <= it.final_line_price || cell.textContent !== money(it.final_line_price)) return;
    cell.innerHTML = `<s class="price-was small">${money(was)}</s> <span class="is-offer">${money(it.final_line_price)}</span>`;
  });
}

// The bag opens at once with the bag last seen, then updates in place when Shopify answers.
// The answer is often already on its way: it is asked for as a pointer or finger reaches the bag.
let lastCart = null, cartRequest = null;
function freshCart() {
  if (!cartRequest) cartRequest = fetchCart().then((c) => { lastCart = c; return c; }).finally(() => { cartRequest = null; });
  return cartRequest;
}
const bagBody = (cart) => `${cartBody(cart)}<div data-bag-extra></div>`;
// Repaints the open bag without a slide-in or a change of scroll position.
function paintBag(cart) {
  lastCart = cart;
  setCount(cart.item_count);
  const sheet = $('.sheet[data-bag]', root);
  if (!sheet) return;
  const body = $('.sheet-body', sheet), top = body.scrollTop;
  body.innerHTML = bagBody(cart);
  body.scrollTop = top;
  let foot = $('.sheet-foot', sheet);
  if (!foot) { foot = document.createElement('div'); foot.className = 'sheet-foot wrap'; sheet.append(foot); }
  foot.innerHTML = cartFoot(cart);
  $('.sheet-head h2', sheet).textContent = `Bag (${cart.item_count})`;
  estimateDelivery();
  markOffers(cart);
  suggestGrout(cart);
}
export async function openCart() {
  const known = lastCart;
  const s = openSheet({ title: known ? `Bag (${known.item_count})` : 'Bag', cls: 'side-sheet', body: known ? bagBody(known) : '<p class="small muted" style="padding:var(--s5) 0">Loading your bag…</p>', foot: (known && cartFoot(known)) || ' ' });
  s.dataset.bag = '1';
  let cart;
  try { cart = await freshCart(); } catch { if (!known) location.href = CFG.routes.cartPage; return; }
  paintBag(cart);
}
const warmBag = () => { freshCart().catch(() => {}); };
$('#bag-btn')?.addEventListener('pointerenter', warmBag, { passive: true });
$('#bag-btn')?.addEventListener('touchstart', warmBag, { passive: true });
$('#bag-btn')?.addEventListener('focus', warmBag);

// Don't forget: with tiles in the bag and no grout yet, the bag offers one grout, small, with its own Add button.
// The grout paired with the tile in Search & Discovery comes first, then the one Shopify's recommendations link to
// the tile from what shoppers buy together, then the first in the grout collection (sort it by Best selling).
const isGrout = (p) => /grout/i.test(`${p.product_type || p.type || ''} ${p.handle || ''}`);
function asGrout(p) {
  const v = p?.variants?.[0];
  if (!v || p.variants.length !== 1 || v.available === false) return null;
  const img = p.featured_image || p.images?.[0]?.src || p.images?.[0] || '';
  // Collection feeds give prices in pounds as text, recommendation feeds in pence.
  return { title: p.title, handle: p.handle, id: v.id, img: typeof img === 'string' ? img : '', price: typeof v.price === 'string' ? Math.round(parseFloat(v.price) * 100) : v.price };
}
let groutList = null;
function groutProducts() {
  if (!groutList) {
    groutList = !CFG.grout ? Promise.resolve([]) : fetch(`${CFG.grout}/products.json?limit=8`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((d) => (d.products || []).map(asGrout).filter(Boolean))
      .catch(() => []);
  }
  return groutList;
}
const groutPicks = new Map();
function pickGrout(tileId) {
  if (!groutPicks.has(tileId)) {
    groutPicks.set(tileId, (async () => {
      if (CFG.routes.recommendations) {
        for (const intent of ['complementary', 'related']) {
          try {
            const r = await fetch(`${CFG.routes.recommendations}?product_id=${tileId}&limit=10&intent=${intent}`, { headers: { Accept: 'application/json' } });
            const match = r.ok ? ((await r.json()).products || []).filter(isGrout).map(asGrout).find(Boolean) : null;
            if (match) return match;
          } catch { /* The collection below still offers a grout. */ }
        }
      }
      return (await groutProducts())[0] || null;
    })());
  }
  return groutPicks.get(tileId);
}
async function suggestGrout(cart) {
  const tile = cart.items.find((it) => /tile/i.test(it.product_type || ''));
  if (!tile || cart.items.some(isGrout)) return;
  const slot = $('[data-bag-extra]', root);
  if (!slot) return;
  const g = await pickGrout(tile.product_id);
  // Ignore a response for a bag that was closed or changed while recommendations loaded.
  if (!g || !slot.isConnected || lastCart !== cart) return;
  slot.innerHTML = `<div class="pdp-suggest bag-suggest"><h4 class="meta">Don't forget grout</h4><div class="mini"><a class="mini-link" href="/products/${esc(g.handle)}"><span class="s-col-img">${g.img ? `<img src="${esc(`${g.img}${g.img.includes('?') ? '&' : '?'}width=120`)}" alt="" loading="lazy" width="48" height="48">` : ''}</span><span class="s-col-t"><b>${esc(g.title)}</b><span class="small num">${money(g.price)}</span></span></a><button type="button" class="chip mini-add" data-act="bag-add" data-id="${g.id}" aria-label="Add ${esc(g.title)} to bag">${icon('plus')} Add</button></div><p class="small muted" style="margin-top:10px">Check the colour and suitability for your project. <a class="link" href="${esc(CFG.grout || '/collections/grout')}">View all grout colours</a></p></div>`;
}

async function changeLine(line, quantity) {
  const res = await fetch(CFG.routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity: Math.max(0, quantity) }) });
  const cart = await res.json();
  if (!res.ok) { toast(cart.description || 'That quantity is not available'); return; }
  // Use a fresh cart total after Shopify recalculates discounts and delivery context.
  try { paintBag(await fetchCart()); } catch { paintBag(cart); }
}

export async function addLine(id, quantity, properties) {
  const res = await fetch(CFG.routes.cartAdd, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity, properties }] }) });
  const data = await res.json();
  // Shopify adds what is in stock and returns 422 for the rest, so the shopper is told.
  if (!res.ok) throw new Error(data.description || 'That quantity is not available');
  lastCart = null;
  return data;
}

// ---- Delivery estimate --------------------------------------------------------------------------------
// Asks Shopify for the rates checkout would offer this bag, so the rules live in one place: Shopify's own
// rates or a delivery app. Prepare, then poll, as Shopify recommends, because app rates can take a moment.
const POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/;
const deliv = { postcode: '', html: '', run: 0, form: 0 };
let deliveryQueue = Promise.resolve();
try { deliv.postcode = canRememberPreferences() ? localStorage.getItem('forme.postcode') || '' : ''; } catch { /* Storage may be blocked. */ }

function deliveryHtml() {
  const id = `deliv-pc-${++deliv.form}`;
  return `<form class="deliv" data-deliv novalidate><label class="small" for="${id}">Delivery</label><input id="${id}" name="postcode" type="text" value="${esc(deliv.postcode)}" placeholder="Your postcode" autocomplete="postal-code" autocapitalize="characters" enterkeyhint="go"><button class="btn btn-quiet" type="submit">Check</button></form><div class="deliv-out small" data-deliv-out aria-live="polite">${deliveryNote('Enter your postcode to check this bag.')}<p><a href="/pages/delivery">Delivery information</a></p></div>`;
}
const deliveryNote = (text) => `<p class="muted">${esc(text)}</p>`;
function deliveryRates(rates) {
  const rows = [...rates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price)).map((r) => {
    const pence = Math.round(parseFloat(r.price) * 100);
    const days = Array.isArray(r.delivery_days) && r.delivery_days.length === 2 ? `<span class="muted">${r.delivery_days[0] === r.delivery_days[1] ? r.delivery_days[0] : r.delivery_days.join('–')} working day${r.delivery_days[1] === 1 ? '' : 's'}</span>` : '';
    return `<li><span>${esc(r.presentment_name || r.name)}${days}</span><span class="num">${pence ? money(pence) : 'Free'}</span></li>`;
  }).join('');
  const standardTiles = rates.some(r => /tile.*7\s*[–-]\s*10/i.test(r.presentment_name || r.name || ''));
  const process = standardTiles ? '<p class="muted">As soon as you place your order, our team starts processing it and aims to deliver your tiles within 7–10 days.</p>' : '';
  return `<ul class="deliv-rates">${rows}</ul>${process}<p class="muted">Delivery estimate for ${esc(deliv.postcode)}. Your final options and charge are confirmed at checkout.</p>`;
}
function estimateDelivery() {
  const run = ++deliv.run;
  const postcode = deliv.postcode;
  const outputs = $$('[data-deliv-out]');
  if (!outputs.length || !postcode) return;
  // A changed bag or postcode must never leave the previous quote on screen.
  deliv.html = '';
  outputs.forEach(out => { out.innerHTML = deliveryNote('Checking delivery…'); out.setAttribute('aria-busy', 'true'); });
  const show = html => {
    if (run !== deliv.run) return;
    deliv.html = html;
    $$('[data-deliv-out]').forEach(out => { out.innerHTML = html; out.removeAttribute('aria-busy'); });
  };
  // Quote in an isolated anonymous cart. Shopify's direct cart estimator can remove
  // products from the real bag when a visitor checks an unsupported region.
  deliveryQueue = deliveryQueue.catch(() => {}).then(async () => {
    if (run !== deliv.run) return;
    try {
      const cart = await fetchCart();
      if (run !== deliv.run) return;
      if (!cart.item_count) {
        show(`${deliveryNote('Add products to your bag to get an order-specific delivery quote.')}<p><a href="/collections/all-tiles">Shop tiles</a> · <a href="/pages/delivery">Delivery information</a></p>`);
        return;
      }
      const res = await fetch('https://hfuk-product-share-pair.kswanborough.workers.dev/storefront/delivery', {
        method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ postcode, items: cart.items.map(item => ({ id: item.variant_id || item.id, quantity: item.quantity })) }),
        signal: AbortSignal.timeout(25000),
      });
      if (!res.ok) throw new Error('quote');
      const data = await res.json();
      if (!Array.isArray(data.shipping_rates)) throw new Error('rates');
      if (run !== deliv.run) return;
      show(data.shipping_rates.length ? deliveryRates(data.shipping_rates) : `${deliveryNote(`No online delivery option is available for ${postcode} and this bag.`)}<p><a href="/pages/contact">Ask our team about delivery</a>.</p>`);
    } catch {
      show(`${deliveryNote('The delivery service could not return a quote. Please try again or check the available options at checkout.')}<p><a href="/pages/contact">Contact us for delivery help</a>.</p>`);
    }
  });
}
document.addEventListener('submit', (e) => {
  if (!e.target.matches?.('[data-deliv]')) return;
  e.preventDefault();
  const input = e.target.elements.postcode;
  const m = POSTCODE.exec(input.value.trim().toUpperCase());
  if (!m) {
    ++deliv.run;
    deliv.html = '';
    $$('[data-deliv-out]').forEach(out => { out.innerHTML = deliveryNote('Enter a full UK postcode, including the last three characters.'); out.removeAttribute('aria-busy'); });
    input.setAttribute('aria-invalid', 'true'); input.focus({ preventScroll: true }); return;
  }
  input.removeAttribute('aria-invalid');
  deliv.postcode = `${m[1]} ${m[2]}`;
  $$('[data-deliv] input[name="postcode"]').forEach(el => { el.value = deliv.postcode; });
  try { if (canRememberPreferences()) localStorage.setItem('forme.postcode', deliv.postcode); } catch { /* Storage may be blocked. */ }
  estimateDelivery();
});

// ---- Search -------------------------------------------------------------------------------------------
const RECENT_KEY = 'forme.recentSearches';
function recentSearches() {
  try { return canRememberPreferences() ? JSON.parse(localStorage.getItem(RECENT_KEY)) || [] : []; } catch { return []; }
}
function saveSearch(q) {
  const term = q.trim();
  if (term.length < 2 || !canRememberPreferences()) return;
  try { localStorage.setItem(RECENT_KEY, JSON.stringify([term, ...recentSearches().filter((x) => x.toLowerCase() !== term.toLowerCase())].slice(0, 5))); } catch { /* Storage may be blocked. */ }
}
// Colours, sizes, shapes and wall or floor, from the store's own tags, as links to those tiles.
const FACET_TITLES = { Colour: 'Shop by colour', Use: 'Wall or floor', Material: 'Material', Size: 'Shop by size' };
let facetLoad = null, facets = [];
function loadFacets() {
  if (!facetLoad) {
    facetLoad = fetch(`${CFG.root || '/'}?section_id=search-facets`).then((r) => (r.ok ? r.text() : '')).then((html) => {
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-facets]');
      facets = el ? JSON.parse(el.textContent) : [];
    }).catch(() => { facets = []; });
  }
  return facetLoad;
}
const facetChip = (f) => `<a class="chip" href="${esc(f.url)}">${f.dot || ''}${esc(f.label)}</a>`;
// Six sizes from smallest to largest, so the whole range is represented.
const SIZE_CHIPS = 6;
function sizeSpread(all) {
  if (all.length <= SIZE_CHIPS) return all;
  const area = (f) => { const a = sizeFromText(f.label); return a ? a.w * a.h : 0; };
  const sorted = [...all].sort((a, b) => area(a) - area(b));
  return Array.from({ length: SIZE_CHIPS }, (_, i) => sorted[Math.round((i * (sorted.length - 1)) / (SIZE_CHIPS - 1))]);
}
function facetGroups() {
  return Object.entries(FACET_TITLES).map(([group, title]) => {
    const all = facets.filter((f) => f.group === group);
    const items = group === 'Size' ? sizeSpread(all) : all.slice(0, 20);
    // Every size can still be found by typing it, and the rest are on the all tiles page.
    const more = items.length < all.length ? `<a class="chip" href="${esc(all[0].url.replace(/\/[^/]*$/, ''))}">More sizes</a>` : '';
    return items.length ? `<div class="s-group"><h4 class="meta">${title}</h4><div class="s-chips">${items.map(facetChip).join('')}${more}</div></div>` : '';
  }).join('');
}
function facetMatches(term) {
  const t = term.toLowerCase(), typed = sizeFromText(term);
  // Sizes match to within 1.5 cm, so 60x60 finds a 59.6 x 59.6 cm tile.
  if (typed) return facets.filter((f) => { const s = f.group === 'Size' && sizeFromText(f.label); return s && ((Math.abs(s.w - typed.w) <= 15 && Math.abs(s.h - typed.h) <= 15) || (Math.abs(s.w - typed.h) <= 15 && Math.abs(s.h - typed.w) <= 15)); });
  return facets.filter((f) => {
    const label = f.label.toLowerCase();
    if (label.startsWith(t) || label.split(/\s+/).some((w) => w.startsWith(t))) return true;
    return t.length >= 3 && (f.group.toLowerCase().startsWith(t) || (FACET_TITLES[f.group] || '').toLowerCase().includes(t));
  }).slice(0, 10);
}
const searchChips = (terms) => `<div class="s-chips">${terms.map((x) => `<button type="button" class="chip" data-q-fill="${esc(x)}">${esc(x)}</button>`).join('')}</div>`;
function searchStart() {
  const recent = recentSearches();
  const groups = facetGroups();
  return `${recent.length ? `<div class="s-group"><div class="s-head"><h4 class="meta">Recent searches</h4><button type="button" class="small muted link" data-q-forget>Clear</button></div>${searchChips(recent)}</div>` : ''}${groups ? `<div class="s-facets">${groups}</div>` : ''}`;
}
function setSearch(q) {
  const input = $('#q');
  input.value = q;
  $('[data-q-clear]').hidden = !q;
  input.focus({ preventScroll: true });
  clearTimeout(runSearch.h);
  runSearch(q);
}
function searchSheet() {
  const sheet = openSheet({
    title: 'Search', cls: 'full search-sheet',
    body: `<form class="search-bar" style="margin-top:-16px" action="${esc(CFG.routes.search)}" method="get" role="search"><span class="muted">${icon('search')}</span><input id="q" type="search" name="q" placeholder="Search by name, finish, shade or size" autocomplete="off" autocorrect="off" enterkeyhint="search" aria-label="Search"><input type="hidden" name="options[prefix]" value="last"><input type="hidden" name="options[unavailable_products]" value="last"><button type="button" class="icon-btn" data-q-clear aria-label="Clear search" hidden>${icon('close')}</button></form><div id="q-results">${searchStart()}</div><p class="sr" id="q-status" role="status"></p>`,
  });
  sheet.addEventListener('click', (e) => {
    const fill = e.target.closest('[data-q-fill]');
    if (fill) { saveSearch(fill.dataset.qFill); setSearch(fill.dataset.qFill); return; }
    if (e.target.closest('[data-q-clear]')) { setSearch(''); return; }
    if (e.target.closest('[data-q-forget]')) { try { localStorage.removeItem(RECENT_KEY); } catch { /* Storage may be blocked. */ } $('#q-results').innerHTML = searchStart(); return; }
    if (e.target.closest('[data-q-retry]')) { runSearch($('#q').value); return; }
    if (e.target.closest('#q-results a')) saveSearch($('#q').value);
  });
  sheet.querySelector('form').addEventListener('submit', () => saveSearch($('#q').value));
  // Up and down move through the results, and from the first result back to the search box.
  sheet.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const links = $$('#q-results a', sheet);
    if (!links.length) return;
    const at = links.indexOf(document.activeElement);
    e.preventDefault();
    if (e.key === 'ArrowDown') links[Math.min(links.length - 1, at + 1)].focus();
    else if (at <= 0) $('#q').focus();
    else links[at - 1].focus();
  });
  $('#q').focus({ preventScroll: true });
  loadFacets().then(() => {
    const out = $('#q-results'), value = $('#q')?.value.trim() || '';
    if (!out) return;
    if (value.length < 2) out.innerHTML = searchStart();
    else runSearch(value);
  });
}
// Marks the words typed. Text is split first and each part escaped, so nothing from a title can become markup.
function highlight(text, q) {
  const words = q.trim().split(/\s+/).filter((w) => w.length > 1).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!words.length) return esc(text);
  const re = new RegExp(`(${words.join('|')})`, 'ig');
  return String(text).split(re).map((part, i) => (i % 2 ? `<mark>${esc(part)}</mark>` : esc(part))).join('');
}
// Shoppers type sizes in many ways. 60x60, 60 x 60 cm and 600*600 all become 600 x 600.
function sizeQuery(q) {
  const m = /^\s*(\d{2,4})\s*(?:x|×|\*|by)\s*(\d{2,4})\s*(cm|mm)?\s*$/i.exec(q);
  if (!m) return null;
  const k = m[3]?.toLowerCase() === 'cm' || (!m[3] && Math.max(+m[1], +m[2]) < 200) ? 10 : 1;
  return `${m[1] * k} x ${m[2] * k}`;
}
async function fetchSuggestions(q, signal) {
  const params = new URLSearchParams({ q, 'resources[type]': 'query,product,collection,article,page', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last', 'resources[options][prefix]': 'last', 'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag,body' });
  const res = await fetch(`${CFG.routes.suggest}?${params}`, { signal, headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(res.status);
  return (await res.json()).resources.results;
}
function searchPrice(p) {
  const min = parseFloat(p.price_min ?? p.price), max = parseFloat(p.price_max ?? p.price);
  if (!Number.isFinite(min)) return '';
  // The old price has to belong to the variant whose price is shown, or the saving would be false.
  const cheapest = (p.variants || []).find((v) => parseFloat(v.price) === min);
  const was = parseFloat(cheapest ? cheapest.compare_at_price : max === min ? p.compare_at_price_min ?? p.compare_at_price : 0);
  const now = `${max > min ? 'From ' : ''}${money(Math.round(min * 100))}`;
  return was > min ? `<span class="is-offer">${esc(now)}</span><s class="price-was">${money(Math.round(was * 100))}</s>` : esc(now);
}
function searchThumb(p) {
  const src = p.featured_image?.url || p.image;
  if (!src) return '';
  return `<img class="in" src="${esc(`${src}${src.includes('?') ? '&' : '?'}width=120`)}" alt="" loading="lazy" decoding="async">`;
}
let searchRequest = null;
async function runSearch(q) {
  const out = $('#q-results');
  if (!out) return;
  searchRequest?.abort();
  const term = q.trim();
  const status = $('#q-status');
  if (term.length < 2) { out.classList.remove('is-busy'); out.innerHTML = searchStart(); status.textContent = ''; return; }
  const request = new AbortController();
  searchRequest = request;
  out.classList.add('is-busy');
  try {
    let shown = term;
    let r = await fetchSuggestions(term, request.signal);
    const size = !r.products?.length && sizeQuery(term);
    if (size && size !== term) {
      r = await fetchSuggestions(size, request.signal);
      shown = size;
    }
    const group = (title, items, row) => (items?.length ? `<div class="s-group"><h4 class="meta">${title}</h4>${items.map(row).join('')}</div>` : '');
    const others = (r.queries || []).filter((x) => x.text.toLowerCase() !== term.toLowerCase()).slice(0, 4);
    const pages = [...(r.articles || []), ...(r.pages || [])];
    const count = (r.products?.length || 0) + (r.collections?.length || 0) + pages.length;
    const all = `${CFG.routes.search}?q=${encodeURIComponent(shown)}&options[prefix]=last&options[unavailable_products]=last`;
    const matched = facetMatches(term);
    const html = (matched.length ? `<div class="s-group"><h4 class="meta">Shop by</h4><div class="s-chips">${matched.map(facetChip).join('')}</div></div>` : '')
      + (others.length ? `<div class="s-group">${searchChips(others.map((x) => x.text))}</div>` : '')
      + group('Tiles', r.products, (p) => `<a class="result press" href="${esc(p.url)}"><span class="ph">${searchThumb(p)}</span><span style="min-width:0"><b>${highlight(p.title, shown)}</b><br><span class="small muted">${esc([p.vendor, p.type].filter(Boolean).join(' · '))}</span></span><span class="small num s-price">${p.available === false ? '<span class="muted">Sold out</span>' : searchPrice(p)}</span></a>`)
      + group('Collections', r.collections, (c) => `<a class="s-link" href="${esc(c.url)}"><span>${highlight(c.title, shown)}</span><span class="muted">→</span></a>`)
      + group('Guides', pages, (a) => `<a class="s-link" href="${esc(a.url)}"><span>${highlight(a.title, shown)}</span><span class="muted">→</span></a>`)
      + (r.products?.length ? `<a class="btn btn-line btn-block s-all" href="${esc(all)}">View all results for “${esc(shown)}”</a>` : '');
    if (request.signal.aborted) return;
    out.innerHTML = count || matched.length ? html : `<div class="empty"><h3>Nothing found for “${esc(term)}”</h3><p class="muted">Check the spelling, or browse by one of these.</p>${facets.length ? `<div class="s-chips">${facets.slice(0, 6).map(facetChip).join('')}</div>` : ''}</div>`;
    status.textContent = count ? `${count} result${count === 1 ? '' : 's'}` : 'No results';
  } catch (err) {
    if (err.name === 'AbortError') return;
    out.innerHTML = `<div class="empty"><h3>Search is not responding</h3><p class="muted">Check your connection and try again.</p><button type="button" class="btn btn-quiet" data-q-retry>Try again</button></div>`;
    status.textContent = 'Search is not responding';
  }
  if (searchRequest === request) out.classList.remove('is-busy');
}

// ---- Product page -------------------------------------------------------------------------------------
function productPage() {
  const shop = window.FORME_SHOPIFY_PRODUCT, tile = window.FORME_PRODUCT;
  if (!shop) return;
  const packFor = (id) => tile.variants.find((v) => String(v.id) === String(id));
  let current = shop.variants.find((v) => String(v.id) === $('#variant-id').value) || shop.variants[0];
  // source says who last set the quantity: null (nobody), 'calc' or 'manual'. The latest action wins.
  const st = { boxes: 1, source: null };
  const calc = { unit: 'm', wastage: 10, surfaces: [{ kind: 'floor', l: '', w: '', openings: [] }] };
  const asCalc = () => calc.surfaces.map((s) => ({ l: s.l, w: s.w, openings: s.openings }));
  const result = () => { const p = packFor(current.id); return p ? calculate({ surfaces: asCalc(), unit: calc.unit, wastagePct: calc.wastage, variant: p }) : null; };

  function paintTotals() {
    const q = $('#qty');
    if (document.activeElement !== q) q.value = st.boxes;
    $('#total').textContent = money(current.price * st.boxes);
  }
  function syncFromCalc() {
    const r = result();
    if (r?.boxes > 0) { st.boxes = r.boxes; st.source = 'calc'; } else if (st.source === 'calc') { st.boxes = 1; st.source = null; }
    paintTotals();
  }
  function paintResult() {
    const el = $('#calc-res');
    if (!el) return;
    const r = result(), p = packFor(current.id);
    if (!r || !r.boxes) { el.innerHTML = `<div><dt>Enter your room size to work out your boxes</dt><dd></dd></div>`; return; }
    const chosen = st.boxes * p.coverage, short = chosen < r.withWastage - 1e-9;
    el.innerHTML = `<div><dt>${round2(r.measured).toFixed(2)} m² + ${calc.wastage}% wastage</dt><dd>${round2(r.withWastage).toFixed(2)} m²</dd></div>
      <div><dt>Boxes, buying ${round2(r.purchased).toFixed(2)} m²</dt><dd><b style="font-weight:500">${r.boxes}</b></dd></div>
      <div><dt>Spare after wastage</dt><dd>${round2(r.excess).toFixed(2)} m²</dd></div>
      <div><dt>Tile cost</dt><dd>${money(r.costPence)}</dd></div>
      ${st.source === 'manual' && st.boxes !== r.boxes ? `<div><dt>${st.boxes} ${st.boxes === 1 ? 'box' : 'boxes'} in your bar: ${round2(chosen).toFixed(2)} m²</dt><dd style="color:${short ? 'var(--danger)' : 'inherit'}">${round2(Math.abs(r.withWastage - chosen)).toFixed(2)} m² ${short ? 'short' : 'spare'}</dd></div><button type="button" class="btn btn-line btn-block" data-calc="use" style="margin-top:8px">Use ${r.boxes} ${r.boxes === 1 ? 'box' : 'boxes'}</button>` : ''}`;
  }
  function paintCalc() {
    const host = $('#calc-host');
    if (!host || !packFor(current.id)) { if (host) host.hidden = true; return; }
    host.hidden = false;
    const dim = (name, val, label, i, j = '') => `<label class="field"><span class="meta">${label}</span><span class="dim"><input class="num" type="number" inputmode="decimal" step="any" min="0" value="${esc(val)}" data-c="${name}" data-i="${i}" data-j="${j}" aria-label="${label}"><i>${calc.unit}</i></span></label>`;
    host.innerHTML = `<div class="results">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap"><span class="meta">How many boxes?</span>
        <div class="seg" role="group" aria-label="Units">${[['m', 'm'], ['cm', 'cm']].map(([k, l]) => `<button type="button" data-calc="unit" data-v="${k}" aria-pressed="${calc.unit === k}">${l}</button>`).join('')}</div></div>
      ${calc.surfaces.map((s, i) => `<div style="display:grid;gap:8px">
        ${calc.surfaces.length > 1 ? `<p class="surf-name meta">Surface ${i + 1}</p>` : ''}
        ${calc.surfaces.length > 1 ? `<div class="surf-head"><div class="seg" role="group" aria-label="Surface type">${['floor', 'wall'].map((k) => `<button type="button" data-calc="kind" data-i="${i}" data-v="${k}" aria-pressed="${s.kind === k}">${k === 'floor' ? 'Floor' : 'Wall'}</button>`).join('')}</div><button type="button" class="small link muted" style="min-height:32px" data-calc="rm" data-i="${i}">Remove</button></div>` : ''}
        <div class="two">${dim('l', s.l, s.kind === 'floor' ? 'Length' : 'Width', i)}${dim('w', s.w, s.kind === 'floor' ? 'Width' : 'Height', i)}</div>
        <div class="calc-btns"><button type="button" class="btn btn-quiet" data-calc="toggle-ops" data-i="${i}" aria-expanded="${!!s.opsOpen}">${icon(s.opsOpen ? 'minus' : 'plus')} Edit Doors/Windows${s.openings.length ? ` (${s.openings.length})` : ''}</button>${i === calc.surfaces.length - 1 ? `<button type="button" class="btn btn-quiet" data-calc="add-surface">${icon('plus')} Add a surface</button>` : ''}</div>
        ${s.opsOpen ? `<div class="calc-ops"><p class="small muted">Tiling a wall? Take off any door or window you won't tile over, so you don't buy boxes you don't need. A door starts at 0.8 × 2 m and a window at 1 × 1 m, so change them to your sizes. Floors don't usually need this.</p>
        ${s.openings.map((o, j) => `<div class="opening">${dim('ol', o.l, o.label, i, j)}${dim('ow', o.w, 'by', i, j)}<label class="field"><span class="meta">Qty</span><input class="num" type="number" min="0" value="${esc(o.count)}" data-c="oc" data-i="${i}" data-j="${j}"></label><button type="button" class="icon-btn" style="width:28px;height:44px" data-calc="rm-op" data-i="${i}" data-j="${j}" aria-label="Remove">${icon('close')}</button></div>`).join('')}
        <div class="st-opts"><button type="button" class="chip" data-calc="op" data-i="${i}" data-v="door">${icon('plus')} Add a door</button><button type="button" class="chip" data-calc="op" data-i="${i}" data-v="window">${icon('plus')} Add a window</button></div></div>` : ''}
      </div>`).join('<hr style="border:0;border-top:1px solid var(--line);width:100%;margin:0">')}
      <div class="st-row"><span class="meta">Wastage</span><div class="seg" role="group" aria-label="Wastage">${[5, 10, 15, 20].map((k) => `<button type="button" class="num" data-calc="waste" data-v="${k}" aria-pressed="${calc.wastage === k}">${k}%</button>`).join('')}</div><p class="small muted">Use 10% for a straight grid and about 15% for herringbone, diagonal layouts or rooms with many cuts.</p></div>
      <dl class="res-list num" id="calc-res"></dl>
    </div>`;
    paintResult();
  }

  const host = $('#calc-host');
  host?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-calc]');
    if (!b) return;
    e.preventDefault();
    const i = +b.dataset.i, f = calc.unit === 'cm' ? 100 : 1;
    switch (b.dataset.calc) {
      case 'unit': {
        if (calc.unit === b.dataset.v) return;
        const k = b.dataset.v === 'cm' ? 100 : 0.01, conv = (n) => (n === '' ? '' : round2(Number(n) * k));
        calc.unit = b.dataset.v;
        for (const s of calc.surfaces) { s.l = conv(s.l); s.w = conv(s.w); for (const o of s.openings) { o.l = conv(o.l); o.w = conv(o.w); } }
        break;
      }
      case 'kind': calc.surfaces[i].kind = b.dataset.v; break;
      case 'rm': calc.surfaces.splice(i, 1); break;
      case 'toggle-ops': calc.surfaces[i].opsOpen = !calc.surfaces[i].opsOpen; break;
      case 'add-surface': calc.surfaces.push({ kind: 'wall', l: '', w: '', openings: [] }); break;
      case 'op': calc.surfaces[i].openings.push(b.dataset.v === 'door' ? { l: 0.8 * f, w: 2 * f, count: 1, label: 'Door' } : { l: f, w: f, count: 1, label: 'Window' }); break;
      case 'rm-op': calc.surfaces[i].openings.splice(+b.dataset.j, 1); break;
      case 'waste': calc.wastage = +b.dataset.v; break;
      case 'use': syncFromCalc(); paintResult(); return;
    }
    syncFromCalc();
    paintCalc();
  });
  host?.addEventListener('input', (e) => {
    const t = e.target, k = t.dataset.c;
    if (!k) return;
    const s = calc.surfaces[+t.dataset.i], o = s.openings[+t.dataset.j];
    t.closest('.field')?.classList.toggle('err', t.value !== '' && !(Number(t.value) >= 0));
    if (k === 'l') s.l = t.value;
    if (k === 'w') s.w = t.value;
    if (k === 'ol') o.l = t.value;
    if (k === 'ow') o.w = t.value;
    if (k === 'oc') o.count = t.value;
    syncFromCalc();
    paintResult();
  });

  // Quantity bar
  $('#buy-form').addEventListener('click', (e) => {
    const b = e.target.closest('[data-step]');
    if (!b) return;
    st.boxes = Math.max(1, st.boxes + +b.dataset.step);
    st.source = 'manual';
    paintTotals();
    paintResult();
  });
  $('#qty').addEventListener('input', (e) => {
    st.boxes = Math.max(1, parseInt(e.target.value, 10) || 1);
    st.source = 'manual';
    $('#total').textContent = money(current.price * st.boxes);
    paintResult();
  });
  $('#buy-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const r = result();
    const properties = st.source === 'calc' && r ? { 'Measured area': `${round2(r.measured).toFixed(2)} m²`, Wastage: `${calc.wastage}%` } : undefined;
    const btn = $('#add-btn');
    btn.disabled = true;
    try { await addLine(current.id, st.boxes, properties); await openCart(); } catch (err) { toast(err.message); }
    btn.disabled = !current.available;
  });

  // Variants
  const pick = [...shop.options].map((_, i) => current.options[i]);
  let tile3D = null;
  function paintVariant(next) {
    const prev = packFor(current.id);
    current = next;
    $('#variant-id').value = next.id;
    try { const u = new URL(location.href); u.searchParams.set('variant', next.id); history.replaceState(null, '', u); } catch { /* Harmless. */ }
    const p = packFor(next.id);
    const on = next.compare_at_price > next.price;
    const main = $('[data-price-main]');
    main.innerHTML = p ? `${money(Math.round(next.price / p.coverage))}<span class="small muted"> /m²</span>` : money(next.price);
    main.classList.toggle('is-offer', on);
    const was = $('[data-price-was]'), tag = $('[data-offer-tag]');
    if (was) { was.hidden = !on; was.textContent = on ? money(p ? Math.round(next.compare_at_price / p.coverage) : next.compare_at_price) : ''; }
    if (tag) { tag.hidden = !on; tag.textContent = on ? `${main.parentNode.dataset.save} ${Math.floor(((next.compare_at_price - next.price) * 100) / next.compare_at_price)}%` : ''; }
    const facts = $('[data-facts]');
    facts.hidden = !p;
    if (p) { $('[data-box-price]').innerHTML = on ? `<span class="is-offer">${money(next.price)}</span> <s class="price-was small">${money(next.compare_at_price)}</s>` : money(next.price); $('[data-coverage]').textContent = `${p.coverage} m²`; $('[data-tiles]').textContent = p.tilesPerBox; }
    const sku = $('[data-sku]'); if (sku) sku.textContent = next.sku ? `SKU ${next.sku}` : '';
    const stock = $('[data-stock]');
    stock.textContent = next.available ? stock.dataset.in : stock.dataset.out;
    stock.classList.toggle('stock-in', next.available);
    stock.classList.toggle('stock-out', !next.available);
    $('#add-btn').disabled = !next.available;
    // New sizes have different pack coverage, so a calculated quantity is worked out again.
    if (st.source === 'calc') syncFromCalc(); else paintTotals();
    paintCalc();
    if (tile3D && p && prev && (p.w !== prev.w || p.h !== prev.h || p.finish !== prev.finish)) { tile3D.dispose(); tile3D = null; start3D(); }
  }
  for (const b of $$('[data-option]')) {
    b.addEventListener('click', () => {
      const oi = +b.dataset.option;
      pick[oi] = b.dataset.value;
      for (const x of $$(`[data-option="${oi}"]`)) x.setAttribute('aria-checked', x === b);
      const next = shop.variants.find((v) => v.options.every((o, i) => o === pick[i]));
      if (next) paintVariant(next); else toast('That combination is not available');
    });
  }

  // Sample
  $('[data-add-sample]')?.addEventListener('click', async (e) => {
    const b = e.currentTarget;
    try { await addLine(+b.dataset.addSample, 1, { 'Sample of': b.dataset.sampleOf }); toast('Sample added to your bag'); const c = await fetchCart(); setCount(c.item_count); } catch (err) { toast(err.message); }
  });

  // Don't forget: one item straight into the bag.
  for (const b of $$('[data-quick-add]')) {
    b.addEventListener('click', async () => {
      b.disabled = true;
      try { await addLine(+b.dataset.quickAdd, 1); toast(`${b.dataset.name} added to your bag`); const c = await fetchCart(); setCount(c.item_count); } catch (err) { toast(err.message); }
      b.disabled = false;
    });
  }

  // Gallery
  const gal = $('#gal'), count = $('#gal-count');
  if (gal && count) {
    const paint = () => { count.textContent = `${Math.round(gal.scrollLeft / Math.max(1, gal.clientWidth)) + 1} / ${gal.children.length}`; };
    gal.addEventListener('scroll', paint, { passive: true });
    paint();
  }

  // The 3D tile loads only when it scrolls into view.
  const holder = $('#tile3d');
  const start3D = async () => {
    const p = packFor(current.id) || tile.variants[0];
    if (!holder || !p) return;
    try {
      const { mountTile3D } = await import('./forme-tile3d.js');
      if (!tile.photo.images.length) throw new Error('No tile photo');
      tile3D = await mountTile3D(holder, tile, p);
      if (!tile3D) throw new Error('No WebGL');
      const hint = $('#tile3d-hint'); if (hint) hint.textContent = '3D tile. Drag to turn it';
    } catch (err) {
      console.warn('3D tile unavailable', err);
      holder.remove();
      const c = $('#gal-count'); if (c && gal) c.textContent = `1 / ${gal.children.length}`;
    }
  };
  if (holder && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) { io.disconnect(); start3D(); } }, { threshold: 0.2 });
    io.observe(holder);
  }
  paintCalc();
  paintTotals();
}

// ---- Page behaviours ----------------------------------------------------------------------------------
function lightbox(startSrc) {
  const srcs = $$('#gal [data-src]').map((b) => b.dataset.src);
  const el = document.createElement('div');
  el.className = 'lightbox dark';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.innerHTML = `<div class="lb-head wrap"><span></span><button class="icon-btn" style="margin-right:-9px" aria-label="Close images" data-lb-close>${icon('close')}</button></div><div class="lb-track" tabindex="0">${srcs.map((s) => `<figure><img src="${esc(s)}" alt="" loading="lazy"></figure>`).join('')}</div>`;
  document.body.append(el);
  document.body.style.overflow = 'hidden';
  const track = $('.lb-track', el);
  requestAnimationFrame(() => (track.scrollLeft = track.clientWidth * Math.max(0, srcs.indexOf(startSrc))));
  const key = (e) => { if (e.key === 'Escape') close(); };
  const close = () => { el.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', key); };
  document.addEventListener('keydown', key);
  $('[data-lb-close]', el).addEventListener('click', close);
  $('[data-lb-close]', el).focus();
}

// Mobile menu. Opening a submenu slides in from the right, and going back slides in from the left.
function menuTree() {
  try { return JSON.parse($('#menu-tree').textContent); } catch { return $$('#nav-main a').map((a) => ({ t: a.textContent, u: a.getAttribute('href'), c: [] })); }
}
function menuSheet() {
  const stack = [{ title: '', items: menuTree() }];
  const sheet = openSheet({ title: 'Menu', cls: 'full', body: `<div class="m-wrap"><nav aria-label="Menu" id="m-panel"></nav><div class="m-foot">${CFG.account ? `<a href="${esc(CFG.account)}">${icon('user')} Account</a>` : ''}${CFG.track ? `<a href="${esc(CFG.track)}">${icon('truck')} Track an order</a>` : ''}<a class="m-logo" href="${esc(CFG.root || '/')}" aria-label="FORME home"><img src="${esc(scriptUrl('forme-footer-logo.svg'))}" alt="" width="1632" height="356"></a></div></div>` });
  const draw = (from) => {
    const panel = $('#m-panel', sheet), level = stack[stack.length - 1], depth = stack.length - 1;
    panel.innerHTML = `${depth ? `<button type="button" class="m-back" data-m-back>${icon('back')} ${esc(level.title)}</button>${level.url && level.url !== '#' ? `<a class="m-all" href="${esc(level.url)}">View all ${esc(level.title)}</a>` : ''}` : ''}${level.items.map((n, i) => (n.c?.length ? `<button type="button" class="m-item" data-m-open="${i}" aria-haspopup="true"><span>${esc(n.t)}</span>${icon('chevron')}</button>` : `<a class="m-item" href="${esc(n.u)}"><span>${esc(n.t)}</span></a>`)).join('')}`;
    if (from) {
      panel.classList.remove('m-from-right', 'm-from-left');
      void panel.offsetWidth;
      panel.classList.add(from === 'right' ? 'm-from-right' : 'm-from-left');
      $('button, a', panel)?.focus({ preventScroll: true });
    }
    $('.sheet-body', sheet).scrollTop = 0;
  };
  sheet.addEventListener('click', (e) => {
    const open = e.target.closest('[data-m-open]');
    if (open) {
      const node = stack[stack.length - 1].items[+open.dataset.mOpen];
      stack.push({ title: node.t, url: node.u, items: node.c });
      draw('right');
      return;
    }
    if (e.target.closest('[data-m-back]')) { stack.pop(); draw('left'); }
  });
  draw();
}

function setDensity(v) {
  $('#grid')?.classList.toggle('one', v === 'one');
  for (const b of $$('[data-act=density]')) b.setAttribute('aria-pressed', b.dataset.v === v);
  try { if (canRememberPreferences()) localStorage.setItem('forme.density', v); } catch { /* Storage may be blocked. */ }
}

document.addEventListener('click', (e) => {
  const el = e.target.closest('[data-act]');
  if (!el) return;
  const act = el.dataset.act;
  if (act === 'close-sheet') { closeSheet(); return; }
  if (act === 'cart') { e.preventDefault(); openCart(); return; }
  if (act === 'menu') { menuSheet(); return; }
  if (act === 'search') { searchSheet(); return; }
  if (act === 'density') { e.preventDefault(); setDensity(el.dataset.v); return; }
  if (act === 'filters') { $('#side')?.classList.add('open'); document.body.style.overflow = 'hidden'; return; }
  if (act === 'filters-close') { $('#side')?.classList.remove('open'); document.body.style.overflow = ''; return; }
  if (act === 'lightbox') { lightbox(el.dataset.src); return; }
  if (act === 'line-step') { e.preventDefault(); changeLine(+el.dataset.line, +el.dataset.q); }
  if (act === 'bag-add') {
    el.disabled = true;
    addLine(+el.dataset.id, 1).then(() => freshCart()).then((c) => { paintBag(c); toast('Grout added to your bag'); }).catch((err) => { toast(err.message); el.disabled = false; });
  }
});
document.addEventListener('input', (e) => {
  if (e.target.id === 'q') { $('[data-q-clear]').hidden = !e.target.value; clearTimeout(runSearch.h); runSearch.h = setTimeout(() => runSearch(e.target.value), 180); }
});
document.addEventListener('change', (e) => {
  const t = e.target;
  if (t.dataset.lineInput) changeLine(+t.dataset.lineInput, parseInt(t.value, 10) || 0);
  if (t.matches('[data-autosubmit]') || t.closest('#side input[type=number]')) t.form?.requestSubmit();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && root.innerHTML) closeSheet();
  if (e.key === 'Tab' && root.innerHTML) {
    const f = $$('a[href], button:not([disabled]), input, select', $('.sheet', root) || root);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
});

try { if (canRememberPreferences() && localStorage.getItem('forme.density') === 'one') setDensity('one'); } catch { /* Storage may be blocked. */ }
const side = $('#side');
if (side) side.insertAdjacentHTML('afterbegin', `<div class="side-head"><span class="meta">Filter</span><button type="button" class="icon-btn" data-act="filters-close" aria-label="Close filters">${icon('close')}</button></div>`);
productPage();
// Images that don't fill their box: colour the spare space to match the photo's own background.
// Reads the edges of the tiny blur-up image, which is already loaded, so this costs almost nothing.
// If the image can't be read, the neutral CSS background stays.
function matchBackground(box) {
  const url = /url\(["']?([^"')]+)/.exec($('img', box)?.style.backgroundImage || '')?.[1];
  if (!url) return;
  const tiny = new Image();
  tiny.crossOrigin = 'anonymous';
  tiny.onload = () => {
    try {
      const n = 16, c = document.createElement('canvas');
      c.width = c.height = n;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(tiny, 0, 0, n, n);
      const px = g.getImageData(0, 0, n, n).data, groups = new Map();
      for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
        if (x > 0 && y > 0 && x < n - 1 && y < n - 1) continue;
        const i = (y * n + x) * 4;
        if (px[i + 3] < 200) continue;
        const key = (px[i] >> 4) << 8 | (px[i + 1] >> 4) << 4 | px[i + 2] >> 4;
        const s = groups.get(key) || { r: 0, g: 0, b: 0, n: 0 };
        s.r += px[i]; s.g += px[i + 1]; s.b += px[i + 2]; s.n++;
        groups.set(key, s);
      }
      if (!groups.size) return;
      const top = [...groups.values()].sort((a, b) => b.n - a.n)[0];
      let [r, gr, b] = [top.r, top.g, top.b].map((v) => Math.round(v / top.n));
      // Compression leaves a white background slightly off-white, which would show as a faint edge.
      if (r > 247 && gr > 247 && b > 247) r = gr = b = 255;
      box.style.backgroundColor = `rgb(${r} ${gr} ${b})`;
    } catch { /* The image can't be read here. The neutral background stays. */ }
  };
  tiny.src = url;
}
$$('.ph-contain').forEach(matchBackground);

const delivHost = $('[data-deliv-host]');
if (delivHost) { delivHost.innerHTML = deliveryHtml(); estimateDelivery(); }

// Reopen Shopify's native consent controls without changing the shopper's choices.
document.addEventListener('click', async e => {
  if (!e.target.closest('[data-cookie-preferences]')) return;
  e.preventDefault();
  try {
    if (!window.privacyBanner?.showPreferences) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js';
        script.onload = resolve; script.onerror = reject; document.head.append(script);
      });
    }
    await window.privacyBanner.showPreferences();
  } catch { toast('Cookie preferences are unavailable. See Cookies & privacy choices for help.'); }
});

// ---- FORME Visualiser ----------------------------------------------------------------------------------
// Opens over the current page, so there is no page load. The logo appears on the tap itself, the 3D loads
// behind it, and it fades away on the first frame.
const VISUALISER_LOGO = `<svg fill="#fff" id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1046.43 565.16"><g><g><path d="M78.67,216.77V70.69q0-6.74-8.36-21.02h124.78v32.88c-16.71-15.9-22.37-15.9-31.8-15.9h-56.33v66.03h46.36c8.35,0,12.67.27,26.68-10.24v37.73c-14.01-10.51-18.33-10.51-26.68-10.51h-46.36v67.65q0,6.74,9.97,21.02h-46.63q8.36-14.55,8.36-21.56Z"/><path d="M211,144c0-56.6,39.89-98.37,97.83-98.37s98.1,41.77,98.1,98.37-40.16,98.37-98.1,98.37-97.83-41.77-97.83-98.37ZM376.47,144c0-51.75-29.38-80.31-67.65-80.31s-67.65,28.57-67.65,80.31,29.65,80.31,67.65,80.31,67.65-28.57,67.65-80.31Z"/><path d="M441.69,216.77V70.69q0-6.74-8.35-21.02h73.85c39.89,0,63.87,20.48,63.87,50.4,0,25.87-18.06,43.66-47.97,48.24l50.67,64.95q10.51,13.48,22.91,25.06h-38l-67.92-88.67h-20.75v67.11q0,7.01,11.32,21.56h-47.97q8.35-14.55,8.35-21.56ZM501.79,135.11c25.6,0,39.62-12.67,39.62-34.77s-14.02-34.77-39.62-34.77h-31.8v69.53h31.8Z"/><path d="M620.91,216.77V70.69q0-6.74-8.35-21.02h34.5l67.11,102.95,66.84-102.95h33.42q-8.36,14.55-8.36,21.56v146.07q0,6.74,8.36,21.02h-48.24q11.86-15.09,11.86-22.37V86.33l-68.99,108.34-68.99-107.8v130.17q0,7.01,10.24,21.29h-37.73q8.35-14.55,8.35-21.56Z"/><path d="M848.37,238.33q8.35-14.55,8.35-21.56V70.69q0-6.74-8.35-21.02h125.05v32.88c-17.25-16.17-22.64-15.9-32.07-15.9h-56.33v63.6h46.09c8.62,0,13.21,0,27.22-10.51v37.73c-14.01-10.51-18.87-10.24-27.22-10.24h-46.09v74.11h51.75c10.51,0,16.44.27,39.35-18.06l-4.31,35.04h-123.44Z"/></g><rect x="285.5" y="120.54" width="46.92" height="46.92"/></g><g><path d="M77.09,292.55h18.3l5.46,14.61,28.26,76.42,28.26-76.58,5.3-14.45h18.14l-7.06,18.3-35.96,94.08h-17.66l-36.44-95.04-6.58-17.34Z"/><path d="M197.49,392.09v-99.54h16.86v112.38h-16.86v-12.84Z"/><path d="M238.75,391.45v-20.07c9.63,13.16,22.48,20.23,37.57,20.23,12.84,0,21.03-6.58,21.03-16.38,0-7.39-4.5-12.84-13.81-16.7l-20.23-8.51c-14.93-6.26-21.51-15.89-21.51-28.74,0-18.62,14.93-31.15,35.48-31.15,14.13,0,25.37,4.33,33.71,11.56v18.94c-7.71-9.47-20.87-15.09-33.39-15.09-10.92,0-18.14,5.78-18.14,14.61,0,6.26,3.53,11.08,13.17,15.09l20.23,8.19c14.77,5.94,22.15,16.38,22.15,30.34,0,20.55-15.73,33.55-39.49,33.55-15.73,0-27.93-5.46-36.76-15.89Z"/><path d="M337.48,361.74v-69.2h16.86v69.2c0,18.46,9.79,29.54,27.29,29.54s27.29-11.08,27.29-29.54v-69.2h16.86v69.2c0,29.06-16.05,45.6-44.15,45.6s-44.15-16.54-44.15-45.6Z"/><path d="M445.69,388.23l38.05-95.69h17.02l38.21,96.17,6.42,16.21h-18.3l-5.62-15.09-5.62-14.93h-47.52l-5.78,15.41-5.3,14.61h-18.14l6.58-16.7ZM510.39,359.82l-18.3-49.13-18.14,49.13h36.44Z"/><path d="M562.56,404.93v-112.38h16.86v96.33h50.73v16.05h-67.59Z"/><path d="M648.93,392.09v-99.54h16.86v112.38h-16.86v-12.84Z"/><path d="M690.19,391.45v-20.07c9.63,13.16,22.48,20.23,37.57,20.23,12.84,0,21.03-6.58,21.03-16.38,0-7.39-4.49-12.84-13.81-16.7l-20.23-8.51c-14.93-6.26-21.51-15.89-21.51-28.74,0-18.62,14.93-31.15,35.48-31.15,14.13,0,25.37,4.33,33.71,11.56v18.94c-7.71-9.47-20.87-15.09-33.39-15.09-10.92,0-18.14,5.78-18.14,14.61,0,6.26,3.53,11.08,13.16,15.09l20.23,8.19c14.77,5.94,22.15,16.38,22.15,30.34,0,20.55-15.73,33.55-39.49,33.55-15.73,0-27.94-5.46-36.77-15.89Z"/><path d="M790.85,404.93v-112.38h69.52v15.25h-52.66v30.82h43.67v15.25h-43.67v35.8h52.66v15.25h-69.52Z"/><path d="M884.93,392.09v-99.54h40.14c22.64,0,36.93,12.52,36.93,31.95,0,16.06-9.63,27.29-25.85,30.82l28.09,34.68,11.88,14.93h-20.55l-38.37-48.48h-15.41v48.48h-16.86v-12.84ZM922.82,342.16c14.61,0,21.83-6.58,21.83-17.66s-7.22-17.66-21.83-17.66h-21.03v35.32h21.03Z"/></g><g><path d="M79.89,531.67v-33.96h12.42c6.21,0,9.75,3.1,9.75,8.29,0,3.64-2.18,6.98-5.97,7.62,4.22.53,7.28,3.74,7.28,8.25,0,6.11-4.07,9.8-11.21,9.8h-12.27ZM96.82,506.78c0-3.06-1.99-5-5.63-5h-6.21v9.94h6.06c3.74,0,5.77-1.89,5.77-4.95ZM98.18,521.62c0-3.78-2.62-5.82-6.84-5.82h-6.35v11.59h6.5c4.17,0,6.69-2.09,6.69-5.77Z"/><path d="M117.49,527.59v-10.87l-8.68-13.87-3.25-5.14h5.87l3.1,5.04,5.58,9.02,5.72-9.22,3.01-4.85h5.72l-3.15,5-8.78,14.02v14.94h-5.14v-4.07Z"/></g><path d="M236.18,515.78c0,10.46-8.59,18.8-19.14,18.8s-19.14-8.35-19.14-18.8,8.53-18.8,19.13-18.8,19.14,8.35,19.14,18.8h0ZM204.73,515.78c0,7,5.49,12.52,12.31,12.52s12.31-5.51,12.31-12.52-5.49-12.52-12.31-12.52-12.31,5.51-12.31,12.52ZM190.51,497.62l-4.5,12.71h6.48l4.64-12.71s-6.61,0-6.61,0ZM176.06,533.93h6.39l-13.63-36.14h-6.92l14.16,36.14h0ZM280.46,497.62v36.31h5.93v-36.31h-5.93ZM150.71,497.62v36.31h5.93v-36.31h-5.93ZM274.55,533.62l-26.86-35.99h-5.93v36.31h5.93v-25.64l19.29,25.37s7.57-.05,7.57-.05Z"/></svg>`;
let splash = null;
function showSplash() {
  if (splash) return;
  splash = document.createElement('div');
  splash.className = 'vz-splash';
  splash.setAttribute('role', 'status');
  splash.innerHTML = `<div class="vz-in"><div class="vz-logo" aria-hidden="true">${VISUALISER_LOGO}</div><p>FORME Visualiser Loading</p><i class="vz-bar"></i></div>`;
  document.body.append(splash);
}
export function hideSplash() {
  const el = splash;
  splash = null;
  if (!el) return;
  el.classList.add('out');
  setTimeout(() => el.remove(), 480);
}
if ($('#app')) showSplash();

const scriptUrl = (name) => new URL(`./${name}`, import.meta.url).href;
let panel = null, panelOpen = false;
const loadVisualiser = () => import('./forme-studio-mount.js');
function storeTileFrom(text) {
  // Only ever text from this store's own theme. It is the same object the product page writes out.
  return new Function(`return (${text.trim()});`)();
}
// Every other store tile, taken from the studio page, so the panel lists the same tiles without delaying it.
async function moreTiles(api) {
  if (!CFG.studio) return;
  try {
    const html = await fetch(CFG.studio, { headers: { Accept: 'text/html' } }).then((r) => r.text());
    const before = window.FORME_CATALOG.length;
    for (const script of new DOMParser().parseFromString(html, 'text/html').querySelectorAll('script')) {
      if (script.textContent.includes('FORME_CATALOG.push')) new Function(script.textContent)();
    }
    if (panelOpen) api.addTiles(window.FORME_CATALOG.slice(before));
  } catch { /* The panel works with the tiles it has. */ }
}
async function openVisualiser(link) {
  if (panelOpen) return;
  panelOpen = true;
  showSplash();
  // Pin the page and size the panel to what is really visible, so Safari's bars can never hide the controls.
  lockPage();
  fitVisualiser();
  document.body.classList.add('in-studio');
  document.body.style.overflow = 'hidden';
  history.pushState({ visualiser: true }, '', '#visualiser');
  try {
    const api = await loadVisualiser();
    if (!panelOpen) return;
    if (!panel) {
      panel = document.createElement('div');
      panel.id = 'visualiser';
      document.body.append(panel);
      panel.addEventListener('click', (e) => {
        if (e.target.closest('.st-top > a')) { e.preventDefault(); history.back(); }
      }, true);
    }
    panel.hidden = false;
    const tile = window.FORME_PRODUCT;
    api.mountStudio(panel, { handle: tile?.id, variant: $('#variant-id')?.value, onReady: hideSplash });
    moreTiles(api);
    sizeSiblings().then((list) => { if (panelOpen) api.addTiles(list.map((s) => s.tile).filter(Boolean)); });
  } catch (err) {
    console.warn('The visualiser could not open here', err);
    location.href = link.href;
  }
}
async function closeVisualiser() {
  if (!panelOpen) return;
  panelOpen = false;
  hideSplash();
  (await loadVisualiser()).leaveStudio();
  if (panel) panel.hidden = true;
  document.body.classList.remove('in-studio');
  document.body.style.overflow = '';
  unlockPage();
}
window.addEventListener('popstate', closeVisualiser);
function fitVisualiser() {
  if (vv) document.documentElement.style.setProperty('--vz-h', `${vv.height}px`);
}
vv?.addEventListener('resize', () => { if (document.body.classList.contains('in-studio')) fitVisualiser(); });
if ($('#app')) fitVisualiser();
const isVisualiserLink = (a) => a.matches('[data-to-studio]') || (!!CFG.studio && !$('#app') && a.pathname === CFG.studio);
document.addEventListener('click', (e) => {
  const a = e.target.closest?.('a[href]');
  if (!a || e.metaKey || e.ctrlKey || e.shiftKey || !isVisualiserLink(a)) return;
  e.preventDefault();
  openVisualiser(a);
});
// Get a head start: as a finger lands, and quietly in idle time on pages that link to it.
document.addEventListener('pointerdown', (e) => {
  const a = e.target.closest?.('a[href]');
  if (a && isVisualiserLink(a)) loadVisualiser().catch(() => {});
}, { passive: true });
if (window.FORME_PRODUCT && !window.FORME_CATALOG.some((t) => t.id === window.FORME_PRODUCT.id)) window.FORME_CATALOG.unshift(window.FORME_PRODUCT);
const warmUp = () => {
  if (navigator.connection?.saveData) return;
  const three = document.querySelector('script[type=importmap]');
  const urls = ['forme-studio-mount.js', 'forme-studio.js', 'forme-engine.js', 'forme-room.js', 'forme-tex.js', 'forme-data.js'].map(scriptUrl);
  if (three) urls.push(JSON.parse(three.textContent).imports.three);
  for (const href of urls) {
    const l = document.createElement('link');
    l.rel = 'modulepreload';
    l.href = href;
    document.head.append(l);
  }
};
if ($('[data-to-studio]')) (window.requestIdleCallback || ((f) => setTimeout(f, 1200)))(warmUp);

// ---- Other sizes --------------------------------------------------------------------------------------
// A tile sold as a separate product with the same name shows as another size, with no setup.
let siblings = null;
function sizeSiblings() {
  if (siblings) return siblings;
  siblings = (async () => {
    const tile = window.FORME_PRODUCT;
    const name = baseName(tile?.name);
    if (!name) return [];
    const params = new URLSearchParams({ q: name, 'resources[type]': 'product', 'resources[limit]': '10', 'resources[options][unavailable_products]': 'last', 'resources[options][fields]': 'title,product_type,variants.title,vendor,tag' });
    const res = await fetch(`${CFG.routes.suggest}?${params}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const products = (await res.json()).resources.results.products || [];
    const own = tile.url.split('?')[0];
    const twins = products.filter((p) => p.url.split('?')[0] !== own && baseName(p.title) === name && (!p.vendor || !tile.range || p.vendor === tile.range));
    return Promise.all(twins.map(async (p) => {
      const url = p.url.split('?')[0];
      let data = null;
      try {
        const html = await fetch(`${url}?section_id=tile-data`).then((r) => r.text());
        data = storeTileFrom(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tile]').textContent);
      } catch { /* The size can still come from the title. */ }
      const sizes = data?.variants?.length ? data.variants.map((v) => ({ w: v.w, h: v.h })) : [sizeFromText(p.title)].filter(Boolean);
      return { url, sizes, tile: data?.variants?.length ? data : null };
    }));
  })().catch(() => []);
  return siblings;
}
async function showOtherSizes() {
  const tile = window.FORME_PRODUCT, shop = window.FORME_SHOPIFY_PRODUCT;
  const block = $('[data-more-sizes]');
  if (!tile || !block) return;
  const list = await sizeSiblings();
  const own = tile.variants.map((v) => ({ w: v.w, h: v.h }));
  const parsed = [...(shop?.variants || []).map((v) => sizeFromText(v.title || (v.options || []).join(' '))), sizeFromText(tile.name)].filter(Boolean);
  const mine = [...own, ...parsed];
  const links = [];
  for (const s of list) for (const size of s.sizes) {
    if (mine.some((m) => sameSize(m, size)) || links.some((l) => sameSize(l.size, size))) continue;
    links.push({ url: s.url, size });
  }
  if (!links.length) return;
  links.sort((a, b) => a.size.w * a.size.h - b.size.w * b.size.h);
  const chips = links.map((l) => `<a class="chip num" href="${esc(l.url)}" aria-label="Also available in ${esc(sizeLabel(l.size))}">${esc(sizeLabel(l.size))}</a>`).join('');
  const sizeOption = (shop?.options || []).findIndex((o) => /size/i.test(typeof o === 'string' ? o : o.name || ''));
  const existing = sizeOption >= 0 ? $(`[data-option="${sizeOption}"]`)?.parentNode : null;
  if (existing) { existing.insertAdjacentHTML('beforeend', chips); return; }
  const current = mine[0] ? `<span class="chip num" aria-pressed="true">${esc(sizeLabel(mine[0]))}</span>` : '';
  $('[data-size-links]', block).innerHTML = current + chips;
  block.hidden = false;
}
// Other sizes wait for idle time, so they never compete with the page first appearing.
if (window.FORME_PRODUCT) (window.requestIdleCallback || ((f) => setTimeout(f, 800)))(() => showOtherSizes().catch(() => {}));

// The next page starts loading as a finger lands or a mouse rests on a link, so it opens sooner.
const prefetched = new Set([location.pathname]);
const prefetch = (e) => {
  const a = e.target.closest?.('a[href]');
  if (!a || navigator.connection?.saveData || a.origin !== location.origin || a.hasAttribute('download')) return;
  if (/^\/(cart|checkout|account|customer_authentication)/.test(a.pathname) || isVisualiserLink(a) || prefetched.has(a.pathname)) return;
  prefetched.add(a.pathname);
  const l = document.createElement('link');
  l.rel = 'prefetch';
  l.href = a.href;
  document.head.append(l);
};
document.addEventListener('touchstart', prefetch, { passive: true });
document.addEventListener('mouseover', (e) => { clearTimeout(prefetch.h); prefetch.h = setTimeout(() => prefetch(e), 90); }, { passive: true });

// ---- Footer panels: open on wider screens, tap to expand on phones --------------------------------------
const footWide = window.matchMedia('(min-width: 768px)');
const syncFooter = () => { for (const d of $$('.foot-panel')) d.open = footWide.matches; };
syncFooter();
footWide.addEventListener('change', syncFooter);

// ---- Home videos, for phones only. Each plays while its slide is on screen. The image stays underneath if it cannot play, for example in Low Power Mode. --
const heroVideos = $$('video[data-mobile-src]');
if (heroVideos.length && window.matchMedia('(max-width: 767px)').matches && !navigator.connection?.saveData && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const videoWatch = new IntersectionObserver((entries) => {
    for (const { target: v, isIntersecting } of entries) {
      if (!isIntersecting) { v.pause(); continue; }
      if (!v.getAttribute('src')) {
        v.muted = true;
        v.addEventListener('playing', () => v.classList.add('playing'), { once: true });
        v.src = v.dataset.mobileSrc;
      }
      v.play().catch(() => {});
    }
  }, { threshold: 0.6 });
  heroVideos.forEach((v) => videoWatch.observe(v));
}

// ---- Home banner slider: swipe, dots, arrows, and optional autoplay that stops once someone takes over --
for (const hero of $$('.hero-slider')) {
  const track = $('.hero-track', hero), slides = $$('.hero-slide', hero), dots = $$('[data-hero-go]', hero);
  const pauseBtn = $('[data-hero-pause]', hero);
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  let index = 0, timer = 0, stopped = false, held = false;
  const focusables = (el) => $$('a, button, input, select, textarea', el);
  const show = (i) => {
    index = i;
    slides.forEach((s, n) => {
      s.setAttribute('aria-hidden', n === i ? 'false' : 'true');
      for (const f of focusables(s)) { if (n === i) f.removeAttribute('tabindex'); else f.setAttribute('tabindex', '-1'); }
    });
    dots.forEach((d, n) => (n === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
  };
  const go = (i) => {
    const n = (i + slides.length) % slides.length;
    track.scrollTo({ left: slides[n].offsetLeft - track.offsetLeft, behavior: still.matches ? 'auto' : 'smooth' });
  };
  // The slide most in view is the current one, whether it got there by swiping, a dot or an arrow.
  const seen = new IntersectionObserver((entries) => {
    for (const e of entries) if (e.isIntersecting) show(slides.indexOf(e.target));
  }, { root: track, threshold: 0.6 });
  slides.forEach((s) => seen.observe(s));
  show(0);
  const seconds = parseFloat(hero.dataset.autoplay || '0');
  const tick = () => { if (!held && !document.hidden) go(index + 1); };
  const play = () => { clearInterval(timer); if (seconds && !stopped && !still.matches) timer = setInterval(tick, seconds * 1000); };
  const stop = () => {
    stopped = true;
    clearInterval(timer);
    if (pauseBtn) { pauseBtn.classList.add('is-paused'); pauseBtn.setAttribute('aria-label', pauseBtn.dataset.labelPlay); }
  };
  hero.addEventListener('click', (e) => {
    if (e.target.closest('[data-hero-prev]')) { stop(); go(index - 1); }
    else if (e.target.closest('[data-hero-next]')) { stop(); go(index + 1); }
    else if (e.target.closest('[data-hero-go]')) { stop(); go(+e.target.closest('[data-hero-go]').dataset.heroGo); }
    else if (e.target.closest('[data-hero-pause]')) {
      if (stopped) { stopped = false; pauseBtn.classList.remove('is-paused'); pauseBtn.setAttribute('aria-label', pauseBtn.dataset.labelPause); go(index + 1); play(); } else stop();
    }
  });
  track.addEventListener('pointerdown', stop, { passive: true });
  track.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stop(); }, { passive: true });
  hero.addEventListener('pointerenter', () => { held = true; });
  hero.addEventListener('pointerleave', () => { held = false; });
  hero.addEventListener('focusin', () => { held = true; });
  hero.addEventListener('focusout', () => { held = false; });
  play();
}

// ---- Tile cards: lifestyle images load as cards near the screen, on devices with a mouse only ----------
function loadLife(img) {
  if (img.getAttribute('src')) return;
  img.onload = () => img.classList.add('ready');
  img.src = img.dataset.life;
}
const canHover = window.matchMedia('(hover: hover)').matches && !navigator.connection?.saveData;
const lifeWatcher = canHover && 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    lifeWatcher.unobserve(entry.target);
    (window.requestIdleCallback || ((f) => setTimeout(f, 100)))(() => loadLife(entry.target));
  }
}, { rootMargin: '300px 0px' }) : null;
const watchLife = (scope = document) => { if (lifeWatcher) for (const img of $$('img[data-life]', scope)) lifeWatcher.observe(img); };
watchLife();
// Reduced data or no observer: load on the first hover instead.
document.addEventListener('pointerover', (e) => {
  if (e.pointerType === 'touch') return;
  const img = e.target.closest?.('.card .ph')?.querySelector('img[data-life]');
  if (img) loadLife(img);
}, { passive: true });

// ---- Endless scroll ----------------------------------------------------------------------------------
// The next page loads shortly before the bottom is reached. The page links stay in the code for search
// engines and browsers without JavaScript, and come back if a page cannot load.
const endlessGrid = $('#grid'), endlessPager = $('.pager[data-next]');
if (endlessGrid && endlessPager && 'IntersectionObserver' in window) {
  let next = endlessPager.dataset.next, busy = false;
  const more = document.createElement('div');
  more.className = 'grid-more';
  more.setAttribute('role', 'status');
  endlessGrid.after(more);
  endlessPager.classList.add('is-endless');
  const load = async () => {
    if (busy || !next) return;
    busy = true;
    more.textContent = 'Loading more tiles…';
    try {
      const res = await fetch(next, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const density = endlessGrid.classList.contains('one');
      endlessGrid.append(...$$('#grid > *', doc).map((n) => document.adoptNode(n)));
      endlessGrid.classList.toggle('one', density);
      watchLife(endlessGrid);
      const pager = $('.pager', doc);
      next = pager?.dataset.next || '';
      endlessPager.innerHTML = pager?.innerHTML || '';
      more.textContent = '';
      busy = false;
      if (!next) { observer.disconnect(); more.remove(); return; }
      // A short page or a tall screen may still leave the marker in view.
      observer.unobserve(more);
      observer.observe(more);
    } catch {
      observer.disconnect();
      more.remove();
      endlessPager.classList.remove('is-endless');
    }
  };
  const observer = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) load(); }, { rootMargin: '900px 0px' });
  observer.observe(more);
}

// Shopify's generated policies open with a date. Show it as a quiet line, not as body text.
if (document.body.classList.contains('template-policy')) {
  const first = $('.shopify-policy__body p');
  if (first && /^last updated/i.test(first.textContent.trim())) first.classList.add('doc-date');
}
